// src/pages/storePages/manageStore/NewCategoryDialog.jsx
import { useEffect, useRef, useState } from "react";
import {
  storeCreateCategory,
  storeUpdateCategory,
  storeGetAllCategories,
} from "../../../storeAPIs/StoreCategoriesApis";
import "../../../static/storecss/manageStore/NewCategoryDialog.css";

/**
 * Modal for creating OR editing a category.
 *
 * Props:
 *  - open:        boolean — controls visibility
 *  - category:    object | null — pass an existing category to edit; null to create
 *  - parentId:    number | string | null — preset parent when creating
 *  - parentName:  string — display name for the preset parent (optional)
 *  - onClose:     () => void
 *  - onSaved:     (saved, mode: "created" | "updated") => void
 */
export default function NewCategoryDialog({
  open,
  category = null,
  parentId = null,
  parentName = "",
  onClose,
  onSaved,
}) {
  const dialogRef = useRef(null);
  const nameInputRef = useRef(null);

  const isEdit = Boolean(category);

  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [parent, setParent] = useState("");
  const [categories, setCategories] = useState([]);
  const [loadingParents, setLoadingParents] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Open / close the native <dialog>
  useEffect(() => {
    const dlg = dialogRef.current;
    if (!dlg) return;

    if (open && !dlg.open) {
      dlg.showModal();
      requestAnimationFrame(() => nameInputRef.current?.focus());
    } else if (!open && dlg.open) {
      dlg.close();
    }
  }, [open]);

  // Reset / hydrate form whenever the dialog opens
  useEffect(() => {
    if (!open) return;

    setError(null);
    setSubmitting(false);
    setSlugTouched(false);

    if (isEdit) {
      setName(category.name ?? "");
      setSlug(category.slug ?? "");
      setParent(category.parent ? String(category.parent) : "");
    } else {
      setName("");
      setSlug("");
      setParent(parentId != null ? String(parentId) : "");
    }
  }, [open, isEdit, category, parentId]);

  // Load categories for the parent dropdown
  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    setLoadingParents(true);
    storeGetAllCategories()
      .then((res) => {
        if (cancelled) return;
        setCategories(res.data?.results ?? res.data ?? []);
      })
      .catch(() => {
        if (!cancelled) setCategories([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingParents(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open]);

  // Auto-generate slug from name until the user touches the slug field
  useEffect(() => {
    if (isEdit || slugTouched) return;
    setSlug(
      name
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s-]/g, "")
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-")
        .replace(/^-+|-+$/g, "")
    );
  }, [name, slugTouched, isEdit]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    setSubmitting(true);
    setError(null);

    const payload = {
      name: name.trim(),
      slug: slug.trim(),
      parent: parent ? Number(parent) : null,
    };

    try {
      let saved;
      if (isEdit) {
        const res = await storeUpdateCategory(category.slug, payload);
        saved = res.data?.results ?? res.data ?? res.data;
        onSaved?.(saved ?? { ...category, ...payload }, "updated");
      } else {
        const res = await storeCreateCategory(payload);
        saved = res.data?.results ?? res.data ?? res.data;
        onSaved?.(saved, "created");
      }
      onClose?.();
    } catch (err) {
      const data = err.response?.data;
      if (data && typeof data === "object") {
        setError(
          Object.entries(data)
            .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`)
            .join(" · ")
        );
      } else {
        setError(err.message || "Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === dialogRef.current && !submitting) {
      onClose?.();
    }
  };

  const title = isEdit ? "Edit category" : "New category";
  const subtitle = isEdit
    ? "Update the name, slug, or parent."
    : parentId && parentName
    ? `Adding inside "${parentName}".`
    : "Add a category to organize products.";

  const parentOptions = categories.filter(
    (c) => !isEdit || c.slug !== category.slug
  );

  return (
    <dialog
      ref={dialogRef}
      className="msr-dialog"
      onClick={handleBackdropClick}
      aria-labelledby="msr-dialog-title"
    >
      <form className="msr-dialog-form" onSubmit={handleSubmit}>
        <header className="msr-dialog-header">
          <div>
            <h2 id="msr-dialog-title">{title}</h2>
            <p>{subtitle}</p>
          </div>
          <button
            type="button"
            className="msr-dialog-close"
            onClick={onClose}
            aria-label="Close dialog"
            disabled={submitting}
          >
            <i className="fa-solid fa-xmark" aria-hidden="true" />
          </button>
        </header>

        <div className="msr-dialog-body">
          <div className="msr-field">
            <label htmlFor="msr-cat-name">Name</label>
            <input
              ref={nameInputRef}
              id="msr-cat-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Electronics"
              required
              maxLength={80}
              autoComplete="off"
            />
          </div>

          <div className="msr-field">
            <label htmlFor="msr-cat-slug">
              Slug
              <span className="msr-field-hint">Used in URLs</span>
            </label>
            <input
              id="msr-cat-slug"
              type="text"
              value={slug}
              onChange={(e) => {
                setSlugTouched(true);
                setSlug(e.target.value);
              }}
              placeholder="electronics"
              required
              maxLength={80}
              pattern="[a-z0-9-]+"
              autoComplete="off"
            />
          </div>

          <div className="msr-field">
            <label htmlFor="msr-cat-parent">Parent category</label>
            <select
              id="msr-cat-parent"
              value={parent}
              onChange={(e) => setParent(e.target.value)}
              disabled={loadingParents}
            >
              <option value="">— None (root category) —</option>
              {parentOptions.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          {error && <div className="msr-dialog-error">{error}</div>}
        </div>

        <footer className="msr-dialog-footer">
          <button
            type="button"
            className="msr-btn msr-btn--secondary"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="msr-btn msr-btn--primary"
            disabled={submitting || !name.trim() || !slug.trim()}
          >
            {submitting
              ? isEdit
                ? "Saving…"
                : "Creating…"
              : isEdit
              ? "Save changes"
              : "Create category"}
          </button>
        </footer>
      </form>
    </dialog>
  );
}